import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { getApiHeader } from 'src/utils';

interface group {
    EventName: string;
    Sequence: string;
    EventAgeGrade: string;
    GroupChestNum: string;
    GroupID: string;
    GroupName: string;
    GroupRegGuardianID: string;
    GroupRegGuardianFirstName: string;
    GroupRegGuardianLastName: string;
    GroupRegGuardianPhoneNumber: string;
    GroupRegGuardianEmail: string;
    ParticpiantCount: string;
    ParticipantFirstNameList: string;
    IsCheckedIn: number | null;
    CheckedinDateTime: string;
}

interface GroupDetailsProps {
    groups: group[];
}

export const GroupDetails: React.FC<GroupDetailsProps> = ({
    groups
}) => {
    const headers = getApiHeader();
    const [
        selectedItems,
        setSelectedItems
    ] = useState<group[]>();
    const [checked, setChecked] = React.useState(false);
    const [validationError, setValidationError] = useState('');

    const { mutate, data, error, isSuccess, isPending, reset: resetData } = useMutation({
        mutationFn: (data: { GroupId?: string, groupChestNumber?: string, checkIn?: number }) => {
            return axios.post('https://ugtc887zai.execute-api.us-east-1.amazonaws.com/Prod/checkingroup',
                data, {
                headers
            });
        },
    });

    const onSaveHandler = () => {
        if (!selectedItems?.[0]?.GroupID || !selectedItems?.[0]?.GroupChestNum) {
            setValidationError('Please select a group to check-in');
        } else {
            setValidationError('');
            mutate({
                GroupId: selectedItems?.[0]?.GroupID,
                groupChestNumber: selectedItems?.[0]?.GroupChestNum,
                checkIn: Number(checked)
            });
        }
    }

    useEffect(() => {
        if (selectedItems?.[0]?.IsCheckedIn === 1) {
            setChecked(true);
        } else {
            setChecked(false);
        }
    }, [selectedItems?.[0]?.IsCheckedIn])

    return (
        <SpaceBetween direction='vertical' size='l'>
            <ExpandableSection
                defaultExpanded
                variant="container"
                headerText="Groups"
            >
                <SpaceBetween direction='vertical' size='l'>
                    <Table
                        selectionType='single'
                        onSelectionChange={({ detail }) =>
                            setSelectedItems(detail.selectedItems)
                        }
                        selectedItems={selectedItems}
                        columnDefinitions={[
                            {
                                id: "alt",
                                header: "Chest number",
                                cell: item => item.GroupChestNum || "-",
                                sortingField: "alt"
                            },
                            {
                                id: "groupName",
                                header: "Group Name",
                                cell: item => item.GroupName || "-"
                            },
                            {
                                id: "eventCategory",
                                header: "Event",
                                cell: item => item.EventName || "-"
                            },
                            {
                                id: "participant names",
                                header: "Participants",
                                cell: item => item.ParticipantFirstNameList || "-"
                            },
                            {
                                id: "grade",
                                header: "Grade",
                                cell: item => item.EventAgeGrade || "-"
                            },
                            {
                                id: "guardianFirstName",
                                header: "Guardian first name",
                                cell: item => item.GroupRegGuardianFirstName || "-"
                            },
                            {
                                id: "guardianLastName",
                                header: "Guardian last name",
                                cell: item => item.GroupRegGuardianLastName || "-"
                            }
                        ]}
                        items={groups}
                        loadingText="Loading resources"
                        sortingDisabled
                        empty={
                            <Box
                                margin={{ vertical: "xs" }}
                                textAlign="center"
                                color="inherit"
                            >
                                <SpaceBetween size="m">
                                    <b>No groups found</b>
                                </SpaceBetween>
                            </Box>
                        }
                    />
                </SpaceBetween>
            </ExpandableSection>
            {
                selectedItems && (
                    <ExpandableSection
                        defaultExpanded
                        variant="container"
                        headerText="Group details"
                    >
                        <SpaceBetween size='l' direction='vertical'>
                            {
                                isSuccess && (
                                    <Alert type='success'>Check-in updated successfully</Alert>
                                )
                            }
                            {
                                validationError || error && (
                                    <Alert type='error'>{validationError || error.message}</Alert>
                                )
                            }

                            <Form
                                actions={
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <Button variant="primary" onClick={onSaveHandler} loading={isPending}>Save</Button>
                                    </SpaceBetween>
                                }
                            >
                                <SpaceBetween direction="horizontal" size="l">
                                    <FormField label="Chest number">
                                        <Input disabled value={selectedItems[0].GroupChestNum} placeholder='Chest number' />
                                    </FormField>
                                    <FormField label="Group Name">
                                        <Input disabled value={selectedItems[0].GroupName} placeholder='First name' />
                                    </FormField>
                                    <FormField label="Grade">
                                        <Input disabled value={selectedItems[0].EventAgeGrade} placeholder='Grade' />
                                    </FormField>
                                    <FormField label="Guardian Phone number">
                                        <Input disabled value={selectedItems[0].GroupRegGuardianPhoneNumber} placeholder='Phone number' />
                                    </FormField>
                                    <FormField label="Guardian First Name">
                                        <Input disabled value={selectedItems[0].GroupRegGuardianFirstName} placeholder='Phone number' />
                                    </FormField>
                                    <FormField label="Guardian Lirst name">
                                        <Input disabled value={selectedItems[0].GroupRegGuardianLastName} placeholder='First name' />
                                    </FormField>
                                    <FormField label="Event">
                                        <Input disabled value={selectedItems[0].EventName} placeholder='First name' />
                                    </FormField>
                                    <FormField label="Participants">
                                        <span title={selectedItems[0].ParticipantFirstNameList}>
                                            <Input disabled value={selectedItems[0].ParticipantFirstNameList} placeholder='First name' />
                                        </span>
                                    </FormField>
                                    {
                                        selectedItems?.[0]?.IsCheckedIn && (
                                            <FormField label="Check-in time">
                                                <Input disabled value={new Date(selectedItems?.[0]?.CheckedinDateTime)?.toLocaleString('en-us', { timeZone: 'America/New_York' })} placeholder='First name' />
                                            </FormField>
                                        )
                                    }
                                    <FormField label="Check-in">
                                        <Checkbox checked={checked} onChange={({ detail }) =>
                                            setChecked(detail.checked)
                                        }>
                                            Check in
                                        </Checkbox>
                                    </FormField>
                                </SpaceBetween>
                            </Form>
                        </SpaceBetween>
                    </ExpandableSection>
                )
            }
        </SpaceBetween>
    )
}